import React, { useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { StackNavigationProp } from '@react-navigation/stack';
import { RouteProp } from '@react-navigation/native';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { PADDING } from '@/constants/residents/homeConstants';
import { useAppSelector } from '@/store';
import { DraggableModal } from '@/components/common/residents/DraggableModal';
import { useScrollableContent } from '@/hooks/residents/useScrollableContent';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useEventRsvp } from '@/hooks/residents/useEventRsvp';

type EventDetailScreenNavigationProp = StackNavigationProp<RootStackParamList, 'EventDetail'>;
type EventDetailScreenRouteProp = RouteProp<RootStackParamList, 'EventDetail'>;

interface EventDetailScreenProps {
  route: EventDetailScreenRouteProp;
  navigation: EventDetailScreenNavigationProp;
}

export const EventDetailScreen: React.FC<EventDetailScreenProps> = ({ 
  route, 
  navigation 
}) => {
  const { event } = route.params;
  const scrollViewRef = useRef<ScrollView>(null);

  // Pull the freshest event from the store so UI reflects RSVP changes
  const storeEvent = useAppSelector((state) => state.home.events.find((e) => e.id === event?.id));
  const viewEvent = storeEvent || event;
  const { toggle, isUpdating } = useEventRsvp(viewEvent, {
    acceptSuccessMessage: 'Marked as going',
    declineSuccessMessage: 'Updated',
  });

  // Guard against undefined event
  if (!event) {
    console.error('EventDetailScreen received undefined event');
    return (
      <View style={{ flex: 1, backgroundColor: MARKETPLACE_COLORS.background, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ color: MARKETPLACE_COLORS.textPrimary }}>Event not found</Text>
      </View>
    );
  }

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleClose = () => {
    navigation.goBack();
  };

  // Use the reusable hook for scrollable content logic
  const {
    canDrag,
    canScroll,
    handleContentSizeChange,
    handleLayout,
  } = useScrollableContent();

  const handleRSVPToggle = async () => {
    await toggle();
  };

  const imageUrl = viewEvent?.photos && viewEvent.photos.length > 0 ? viewEvent.photos[0]?.photoUrl : undefined;
  const description = viewEvent?.description || viewEvent?.responseDescription || '';

  return (
    <DraggableModal 
      onClose={handleClose} 
      canDrag={canDrag}
      testID="event-detail-modal"
    >
      {isUpdating && (
        <View style={styles.loadingOverlay}>
          <LoadingSpinner size="large" showMessage={false} variant="resident" />
        </View>
      )}
      {/* Always render ScrollView to get proper measurements */}
      <ScrollView 
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        scrollEventThrottle={16}
        scrollEnabled={canScroll}
        testID="event-detail-scrollview"
      >
        {/* Header Image */}
        {imageUrl && (
          <Image
            source={{ uri: imageUrl }}
            style={styles.headerImage}
            resizeMode="cover"
          />
        )}
        
        {/* Content */}
        <View style={styles.contentContainer}>
          {/* Event Title */}
          <Text style={styles.title}>
            {viewEvent?.name || 'Event'}
          </Text>
          
          {/* Date & Time */}
          <View style={styles.infoRow}>
            <Icon name="time-outline" size={20} color={MARKETPLACE_COLORS.textMuted} />
            <Text style={styles.infoText}>
              {formatDateTime(viewEvent?.startTime)}
            </Text>
          </View>
          
          {/* Location */}
          {viewEvent?.location && (
            <View style={styles.infoRow}>
              <Icon name="location-outline" size={20} color={MARKETPLACE_COLORS.textMuted} />
              <Text style={styles.infoText}>{viewEvent.location}</Text>
            </View>
          )}
          
          {/* Address */}
          {viewEvent?.address && (
            <View style={styles.infoRow}>
              <Icon name="map-outline" size={20} color={MARKETPLACE_COLORS.textMuted} />
              <Text style={styles.infoText}>{viewEvent.address}</Text>
            </View>
          )}
          
          {/* RSVP Section */}
          <View style={styles.rsvpSection}>
            <Text style={styles.rsvpTitle}>Will you attend?</Text>
            <TouchableOpacity 
              style={[
                styles.rsvpButton,
                viewEvent?.rsvp && styles.rsvpButtonActive
              ]}
              onPress={handleRSVPToggle}
            >
              <Icon 
                name={viewEvent?.rsvp ? "checkmark-circle" : "checkmark-circle-outline"} 
                size={24} 
                color={viewEvent?.rsvp ? "#FFFFFF" : MARKETPLACE_COLORS.accentBlueSolid} 
              />
              <Text style={[
                styles.rsvpButtonText,
                viewEvent?.rsvp && styles.rsvpButtonTextActive
              ]}>
                {viewEvent?.rsvp ? 'Going' : 'RSVP'}
              </Text>
            </TouchableOpacity>
          </View>
          
          {/* Description */}
          {description && (
            <View style={styles.descriptionSection}>
              <Text style={styles.sectionTitle}>About</Text>
              <Text style={styles.description}>{description}</Text>
            </View>
          )}
          
          {/* RSVP Count */}
          {viewEvent.nRsvps !== undefined && (
            <View style={styles.rsvpCountSection}>
              <Text style={styles.rsvpCountText}>
                {viewEvent.nRsvps} {viewEvent.nRsvps === 1 ? 'person is' : 'people are'} going
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </DraggableModal>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: PADDING.padding6,
  },
  headerImage: {
    width: '100%',
    height: 250,
  },
  contentContainer: {
    padding: PADDING.padding4,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: PADDING.padding4,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: PADDING.padding3,
    gap: PADDING.padding2,
  },
  infoText: {
    fontSize: 16,
    color: MARKETPLACE_COLORS.textPrimary,
    flex: 1,
  },
  rsvpSection: {
    marginTop: PADDING.padding6,
    marginBottom: PADDING.padding4,
  },
  rsvpTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: PADDING.padding3,
  },
  rsvpButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: MARKETPLACE_COLORS.accentBlueSolid,
    borderRadius: 12,
    paddingVertical: PADDING.padding3,
    paddingHorizontal: PADDING.padding4,
    gap: PADDING.padding2,
  },
  rsvpButtonActive: {
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
  },
  rsvpButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.accentBlueSolid,
  },
  rsvpButtonTextActive: {
    color: '#FFFFFF',
  },
  descriptionSection: {
    marginTop: PADDING.padding4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: PADDING.padding3,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    color: MARKETPLACE_COLORS.textPrimary,
  },
  rsvpCountSection: {
    marginTop: PADDING.padding4,
    padding: PADDING.padding3,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 8,
  },
  rsvpCountText: {
    fontSize: 14,
    color: MARKETPLACE_COLORS.textMuted,
    textAlign: 'center',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
});